import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import { parseRmaNo, rmaNo, type DeviceRmaRequest } from '@staff-app/shared-types';
import { useSetDeviceRmaDismissed } from '../hooks/useRma';

/** The four reasons, with what each one actually means to whoever reads the row later. */
const REASONS = [
  {
    value: 'DUPLICATE',
    label: 'Duplicate',
    hint: 'The same fault, filed twice. Name the request that stands — that is what makes this a merge rather than a hide.',
  },
  { value: 'TEST', label: 'Test / internal', hint: 'Filed by us while testing, not a customer request.' },
  { value: 'WITHDRAWN', label: 'Withdrawn', hint: 'The customer no longer wants it — fixed itself, or they changed their mind.' },
  { value: 'NOT_AN_RMA', label: 'Not an RMA', hint: 'Came through the form but is something else — a question, a sales enquiry.' },
] as const;

/**
 * RmaDismissDialog — take a request out of the working queue, or put it back.
 *
 * ⚠️ IT IS NOT A DELETE, and could not be. This table is a projection of HubSpot form submissions
 * re-polled every five minutes, and the ingest cursor is `max(submitted_at)` minus an overlap — so
 * deleting an OLD row would stick while deleting the NEWEST one would see it reappear within five
 * minutes. A button that works most of the time and silently undoes itself the rest is worse than
 * no button. Dismissal is staff-owned, so it survives every tick.
 *
 * It is also what the tools this queue resembles do: Zendesk merges duplicates into a primary
 * ticket, Jira resolves them against a canonical issue, Shopify cancels rather than deletes an
 * order, Stripe voids rather than deletes an invoice. The record came from somewhere else, and it
 * is evidence — somebody asks in three months why a customer got two replacements.
 *
 * DISMISSED IS A STATE, NOT A FILTER (requester, 2026-09-22). The server refuses verification, the
 * replacement lane, the return receipt, the ship-to correction and the restocking fee on a
 * dismissed row. Restoring is how you take it back.
 */
export function RmaDismissDialog({
  row,
  onClose,
}: {
  row: DeviceRmaRequest;
  onClose: () => void;
}) {
  const [dismiss, { loading }] = useSetDeviceRmaDismissed();
  const [reason, setReason] = useState<string>(row.dismissedReason ?? '');
  const [duplicateOf, setDuplicateOf] = useState(
    row.duplicateOfRmaNumber ? String(row.duplicateOfRmaNumber) : '',
  );
  const [error, setError] = useState<string | null>(null);

  // Accepts `RMA-1042`, `rma 1042`, `#1042` or `1042` — the same four shapes the search box takes,
  // because this number gets pasted from a ticket or read off another row.
  const canonical = parseRmaNo(duplicateOf);
  const needsCanonical = reason === 'DUPLICATE';
  const canSave = Boolean(reason) && (!needsCanonical || canonical !== undefined) && !loading;

  const run = async (nextReason: string | null) => {
    setError(null);
    try {
      await dismiss({
        variables: {
          submissionId: row.submissionId,
          reason: nextReason,
          duplicateOfRmaNumber: nextReason === 'DUPLICATE' ? (canonical ?? null) : null,
        },
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col" data-testid="rma-dismiss-dialog">
        <DialogHeader>
          <DialogTitle>
            {row.dismissed ? `Restore ${rmaNo(row.rmaNumber)}` : `Dismiss ${rmaNo(row.rmaNumber)}`}
          </DialogTitle>
        </DialogHeader>

        <div className="-mr-2 flex flex-col gap-4 overflow-y-auto pr-2">
          {/* The already-dismissed case is a different question — "do you want this back?" — so it
              gets its own body rather than a form with the reason pre-selected. */}
          {row.dismissed ? (
            <div className="flex flex-col gap-2 text-sm" data-testid="rma-dismiss-current">
              <p>
                {`Dismissed as ${
                  REASONS.find((r) => r.value === row.dismissedReason)?.label ?? row.dismissedReason
                } by ${row.dismissedBy ?? 'unknown'}${
                  row.dismissedAt ? ` on ${new Date(row.dismissedAt).toLocaleString()}` : ''
                }.`}
              </p>
              {row.duplicateOfRmaNumber ? (
                <p className="text-muted-foreground">
                  {`Recorded as a duplicate of ${rmaNo(row.duplicateOfRmaNumber)}.`}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {'Restoring puts it back in the working queue and makes it actionable again — verification, replacement and the return receipt all refuse while it is dismissed.'}
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {'This does not delete the request — the row stays, and the HubSpot poll keeps refreshing it. It comes out of the working queue and stops being actionable.'}
              </p>

              <div className="flex flex-col gap-2">
                {REASONS.map((r) => (
                  <label
                    key={r.value}
                    className={`cursor-pointer rounded-md border p-2 ${
                      reason === r.value ? 'border-primary bg-muted' : 'hover:border-muted-foreground/70'
                    }`}
                  >
                    <input
                      type="radio"
                      name="rma-dismiss-reason"
                      className="sr-only"
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      data-testid={`rma-dismiss-${r.value}`}
                    />
                    <span className="text-sm font-medium">{r.label}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{r.hint}</span>
                  </label>
                ))}
              </div>

              {/* Only on DUPLICATE, and REQUIRED there: without it this is a hide, and nobody can
                  tell later which of the two requests actually got worked. */}
              {needsCanonical ? (
                <div data-testid="rma-dismiss-canonical">
                  <Label htmlFor="rma-duplicate-of" className="text-xs">
                    {'Duplicate of'}
                  </Label>
                  <Input
                    id="rma-duplicate-of"
                    value={duplicateOf}
                    onChange={(e) => setDuplicateOf(e.target.value)}
                    placeholder="RMA-1042"
                    className="mt-1 font-mono"
                    data-testid="rma-duplicate-of"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {canonical
                      ? `Will record this as a duplicate of ${rmaNo(canonical)}. That request must exist and must not itself be dismissed.`
                      : 'The RMA number of the request that stands — RMA-1042, #1042 or 1042.'}
                  </p>
                </div>
              ) : null}
            </>
          )}

          {error ? (
            <p className="text-sm text-destructive" data-testid="rma-dismiss-error">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={onClose} data-testid="rma-dismiss-cancel">
            {'Cancel'}
          </Button>
          {row.dismissed ? (
            <Button disabled={loading} onClick={() => void run(null)} data-testid="rma-dismiss-restore">
              {loading ? 'Restoring…' : 'Restore to the queue'}
            </Button>
          ) : (
            <Button disabled={!canSave} onClick={() => void run(reason)} data-testid="rma-dismiss-save">
              {loading ? 'Dismissing…' : 'Dismiss'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
